/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-underscore-dangle */
import { useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import Select from '../../../juristec-ui/core/SelectNew';
import Button from '../../../juristec-ui/core/Button';
import Avatar from '../../../juristec-ui/core/Avatar';
import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';
import PublicLink from '../../PublicLink';

import {
  ShareContainer,
  UserShareRow,
  UserProfile,
  UserInfo,
  UserList,
  UserListItem,
  SelectWrapper,
} from './styled/ShareList.styled';
import { User as BaseUser, Group, SelectOption } from '../../../types';
import IconButton from '../../../juristec-ui/core/IconButton';
import { Trash } from '../../../juristec-ui/icons';
import Tooltip from '../../../juristec-ui/core/Tooltip';

interface User extends BaseUser {
  sharePermission?: {
    value: string;
    label: string;
  };
  label: string;
  value: string;
  id: string
}

interface SharedWith {
  [key: string]: {
    value: string;
    sharedAt: {
      seconds: number;
      nanoseconds: number;
    };
  };
}

interface PermissionOption {
  label: string;
  value: string;
}

interface TourContext {
  tourOpen: boolean;
  nextStep: () => void;
  refreshTour?: () => void;
}

interface SelectedGroup extends Group {
  value: string;
  label: string;
  sharePermission: SelectOption
}

interface ShareListProps {
  owner: string;
  submit: (selected: User[], groups: SelectedGroup[], removedItems: string[]) => void;
  close: () => void;
  genPublicUrl: (
    operation: string,
    newPublicUrl?: string,
    pwd?: string,
    expDt?: string
  ) => Promise<unknown>;
  groups?: Group[];
  permissionsOpts: PermissionOption[];
  publicUrl?: string;
  publicTimestamp?: string | object | number;
  users?: User[];
  publicLink?: boolean;
  sharedWith?: SharedWith;
  permissionsPlan?: Record<string, unknown>;
  expiresAt?: string;
  passwordProtected?: boolean;
  getAllInfos?: () => Promise<unknown>;
  tourContext?: TourContext;
}

const defaultProp = {
  groups: [],
  publicUrl: '',
  publicTimestamp: '',
  users: [],
  publicLink: true,
  sharedWith: {},
  permissionsPlan: {},
  passwordProtected: false,
  getAllInfos: () => Promise.resolve({}),
  tourContext: { tourOpen: false, nextStep: () => {} },
};

export const ShareList = ({
  owner,
  submit,
  close,
  genPublicUrl,
  permissionsOpts,
  groups = defaultProp.groups,
  publicUrl = defaultProp.publicUrl,
  publicTimestamp = defaultProp.publicTimestamp,
  users = defaultProp.users,
  publicLink = true,
  sharedWith = defaultProp.sharedWith,
  permissionsPlan = defaultProp.permissionsPlan,
  expiresAt,
  passwordProtected = defaultProp.passwordProtected,
  getAllInfos = defaultProp.getAllInfos,
  tourContext = defaultProp.tourContext,
}: ShareListProps) => {
  const [usersList, setUsersList] = useState<User[]>([]);
  const [groupsList, setGroupsList] = useState<SelectedGroup[]>([]);
  const [selected, setSelected] = useState<User[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>([]);
  const [userOwner, setUserOwner] = useState<User | null>(null);
  const [_passwordProtected, _setPasswordProtected] = useState<boolean>(passwordProtected);
  const theme = useTheme();

  useEffect(() => {
    const sharedList: User[] = [];
    const sharedGroupsList: SelectedGroup[] = [];
    setUsersList(users.reduce((uList: User[], user: User) => {
      if (user.id !== owner) {
        const fUser: User = {
          ...user,
          value: user.id,
          label: user.name,
          sharePermission: permissionsOpts.find(
            (opt) => opt.value === (sharedWith?.[user.id]?.value),
          ) || permissionsOpts[0],
        };
        if (sharedWith?.[user.id]) sharedList.push(fUser);
        uList.push(fUser);
      } else {
        setUserOwner(user);
      }
      return uList;
    }, []));
    setGroupsList(groups.reduce((gList: SelectedGroup[], group: Group) => {
      const fGroup: SelectedGroup = {
        ...group,
        label: group.name,
        value: group.id,
        sharePermission: permissionsOpts.find(
          (opt) => opt.value === (sharedWith?.[group.id]?.value),
        ) || permissionsOpts[0],
      };
      if (sharedWith?.[group.id]) sharedGroupsList.push(fGroup);
      gList.push(fGroup);
      return gList;
    }, []));
    setSelected(sharedList);
    setSelectedGroups(sharedGroupsList);
  }, [users, sharedWith, groups]);

  const handleTempSelecteds = (optns: string | SelectOption | SelectOption[]) => {
    setSelected(optns as User[]);
  };
  const handleGroupSelect = (optns: string | SelectOption | SelectOption[]) => {
    setSelectedGroups(optns as SelectedGroup[]);
  };

  const handlePermission = (param: PermissionOption, id: string) => {
    setSelected((old) => old.map((user) => (
      user.value === id ? {
        ...user, sharePermission: { value: param.value, label: param.label },
      } : user
    )));
  };

  const handleGroupPermissions = (param: PermissionOption, id: string) => {
    setSelectedGroups((old) => old.map((group) => (
      group.value === id ? {
        ...group, sharePermission: { value: param.value, label: param.label },
      } : group
    )));
  };

  const handleSubmitData = () => {
    let oldShared = { ...sharedWith };
    selected.forEach((user) => {
      const { [user.value]: _, ...newObj } = oldShared;
      oldShared = newObj;
    });
    selectedGroups.forEach((group) => {
      const { [group.value]: _, ...newObj } = oldShared;
      oldShared = newObj;
    });

    submit(selected, selectedGroups, Object.keys(oldShared));
  };

  useEffect(() => {
    if (tourContext.tourOpen && tourContext.refreshTour) {
      tourContext.refreshTour();
    }
  }, [selected, tourContext]);

  const _genPublicUrl = async (
    operation: string, newPublicUrl?: string, pwd?: string, expDt?: string,
  ) => {
    const res = await genPublicUrl(operation, newPublicUrl, pwd, expDt);
    const resInfo = await getAllInfos();
    // @ts-expect-error not typed yet
    _setPasswordProtected(resInfo?.passwordProtected);
    return res;
  };

  return (
    <>
      <ShareContainer>
        <UserShareRow>
          <Select
            label="Compartilhar com"
            placeholder="Selecionar usuário(s)"
            options={usersList}
            fullWidth
            placement="start"
            // @ts-expect-error not typed yet
            value={selected}
            clearable
            onChange={handleTempSelecteds}
            multiple
            atModal
            searchable
            stayOpen
            sortBy="ascending"
          />
        </UserShareRow>
        <UserShareRow>
          <Select
            label=""
            placeholder="Selecionar grupo(s)"
            options={groupsList}
            fullWidth
            placement="start"
            // @ts-expect-error not typed yet
            value={selectedGroups}
            clearable
            onChange={handleGroupSelect}
            multiple
            atModal
            searchable
            stayOpen
            sortBy="ascending"
          />
        </UserShareRow>
        <UserList className="list_share_user">
          {userOwner && (
            <UserListItem>
              <UserProfile>
                <Avatar
                  name={userOwner.name || userOwner.email}
                  size="medium"
                  // @ts-expect-error not typed yet
                  src={userOwner.photoUrl || null}
                />
                <UserInfo>
                  <span>{userOwner.name || userOwner.email}</span>
                  <span>{userOwner.email}</span>
                </UserInfo>
              </UserProfile>
              <SelectWrapper>
                <span style={{ width: '100px', margin: 'auto' }}>Proprietário</span>
              </SelectWrapper>
            </UserListItem>
          )}
          {selected.map((user) => (
            <UserListItem key={user.value}>
              <UserProfile>
                <Avatar
                  name={user.label || user.email}
                  size="medium"
                  // @ts-expect-error not typed yet
                  src={user.photoUrl || null}
                />
                <UserInfo>
                  <span>{user.label || user.email}</span>
                  <span>{user.email}</span>
                </UserInfo>
              </UserProfile>
              <SelectWrapper>
                {user.role === 'guest' ? (
                  <span style={{ width: '100px', margin: 'auto' }}>Visualização</span>
                ) : (
                  <Select
                    options={permissionsOpts}
                    placement="center"
                    placeholder="Selecione"
                    fullWidth
                    // @ts-expect-error not typed yet
                    value={user.sharePermission}
                    onChange={
                      (
                        optn: SelectOption | SelectOption[] | string | null,
                      ) => handlePermission(optn as SelectOption, user.value!)
                    }
                    atModal
                    style={{ textAlign: 'center' }}
                  />
                )}
              </SelectWrapper>
              <IconButton onClick={
                () => setSelected((old) => old.filter((u) => u.value !== user.value))
              }
              >
                <Trash />
              </IconButton>
            </UserListItem>
          ))}
          {selectedGroups.map((group) => (
            <UserListItem key={group.value}>
              <Tooltip atModal text="Grupo">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    // @ts-expect-error not typed yet
                    backgroundColor: theme.primary,
                    borderRadius: '50%',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 500,
                  }}
                >
                  G
                </div>
              </Tooltip>
              <UserProfile>
                <UserInfo>
                  <span>{group.label}</span>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100px',
                    }}
                    title={group.description}
                  >
                    {group.description}
                  </span>
                </UserInfo>
              </UserProfile>
              <SelectWrapper>
                {(
                  <Select
                    options={permissionsOpts}
                    placement="center"
                    placeholder="Selecione"
                    fullWidth
                    // @ts-expect-error not typed yet
                    value={group.sharePermission}
                    onChange={
                      (
                        optn: SelectOption | SelectOption[] | string | null,
                      ) => handleGroupPermissions(optn as SelectOption, group.value!)
                    }
                    atModal
                    style={{ textAlign: 'center' }}
                  />
                )}
              </SelectWrapper>
              <IconButton onClick={
                () => setSelectedGroups((old) => old.filter((g) => g.value !== group.value))
              }
              >
                <Trash />
              </IconButton>
            </UserListItem>
          ))}
        </UserList>

      </ShareContainer>
      <hr style={{ width: '75%', margin: '1rem auto 5px' }} />
      {publicLink && (
        <PublicLink
          link={publicUrl}
          // @ts-expect-error not typed yet
          timestamp={publicTimestamp}
          // @ts-expect-error not typed yet
          genPublicUrl={_genPublicUrl}
          permissions={permissionsPlan}
          // @ts-expect-error not typed yet
          expiresAt={expiresAt}
          passwordProtected={_passwordProtected}
        />
      )}
      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={close}
          variant="outlined"
          size="large"
        >
          Cancelar
        </Button>
        <Button
          style={{ margin: '5px' }}
          onClick={handleSubmitData}
          size="large"
        >
          Concluir
        </Button>
      </ActionsGroup>
    </>
  );
};

export default ShareList;
